import { useState, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useParams, useLocation } from 'react-router'
import { useTheme } from '@mui/material/styles'

import { queries } from '../../graphql'
import { AppBar, Box, Button, Container, Icons, Link, Menu, MenuItem, NavTabs, Text, Toolbar, Typography } from '..'
import { useAuth, usePermissions, useMenu, useAdvisorAccess } from '../../hooks'
import { useBranding } from '../../hooks'
import { ContextSwitcher } from '../context-switcher/context-switcher'
import { computeContexts, detectActiveContext } from '../context-switcher/compute-contexts'

const userLinks = [{ to: '/profile', label: 'My Profile' }]

export function Navbar() {
  const [{ isAdmin, isImpersonating, impersonationType }, { logout }] = useAuth()
  // Check RBAC permissions for management portal access
  const { hasPermission: hasManageAccess } = usePermissions(['manage:access', 'system:admin'])
  const [anchorElUser, setAnchorElUser] = useState(null)
  const { fundId, id: charityIdFromParams } = useParams()
  const { data: { me: { fund = {} } = {} } = {} } = useQuery(queries.funds.myFund)
  const mainFundId = fund?.id
  const { data: meData } = useQuery(queries.user.me)
  const isCharityUser = (meData?.me?.charityUsers || []).length > 0
  const currentFundId = fundId || mainFundId
  const theme = useTheme()
  const branding = useBranding()
  const { isAdvisor, loading: advisorLoading } = useAdvisorAccess()

  // Fetch menu items from Menu API
  // FLIGHT-570: menu type is driven by the ACTIVE PORTAL (URL), not user-level
  // flags. Without this, a multi-portal user (Nancy: donor+advisor+charity+admin)
  // who switches to Charity still sees the Advisor menu because isAdvisor stays true.
  const { pathname } = useLocation()
  const activePortal = detectActiveContext(pathname)

  const contexts = useMemo(() => computeContexts({ me: meData?.me }), [meData?.me])
  const effectivePortal = useMemo(() => {
    if (activePortal) return activePortal
    return (
      contexts.find((c) => c.kind === 'fund') ??
      contexts.find((c) => c.kind === 'charity') ??
      contexts.find((c) => c.kind === 'advisor') ??
      null
    )
  }, [activePortal, contexts])

  const charityId = effectivePortal?.kind === 'charity' ? String(effectivePortal.id) : charityIdFromParams

  const isAdvisorImpersonating = isImpersonating && impersonationType === 'ADVISOR'
  const impersonationLoading = isImpersonating && !impersonationType
  const getMenuType = () => {
    if (impersonationLoading) return null
    if (advisorLoading) return null
    if (isAdvisorImpersonating) return 'NAVBAR'

    // Active portal drives the menu shell. URL is the source of truth post-switch.
    if (activePortal?.kind === 'admin') return null // sidebar owns admin nav; navbar shows nothing
    if (activePortal?.kind === 'charity') return 'PORTAL'
    if (activePortal?.kind === 'advisor') return 'ADVISOR'
    if (activePortal?.kind === 'fund') return 'NAVBAR'

    // Fallback for single-portal users with no URL-derived context yet (e.g. fresh login)
    if (effectivePortal?.kind === 'advisor') return 'ADVISOR'
    if (effectivePortal?.kind === 'charity') return 'PORTAL'
    return 'NAVBAR'
  }
  const menuType = getMenuType()
  const { menuItems, loading: menuLoading, error: menuError } = useMenu(menuType)

  // Check if navbar logo should be shown (defaults to false)
  const showNavbarLogo = branding?.showNavbarLogo === true
  const logoSrc = branding?.getAssetValue('logo') || '/givewise-logo-wordmark.png'

  // Transform menu items to nav links with dynamic route parameters
  const fundLinks = useMemo(() => {
    // Don't show fallback menu while still determining user type (advisor vs donor)
    // This prevents showing wrong menu items during state transitions (e.g., after switchBack)
    if (advisorLoading || menuLoading) {
      return []
    }

    // FLIGHT-570: on the admin portal, the management Sidebar owns nav. Navbar hides its menu.
    if (activePortal?.kind === 'admin') return []

    // Active portal kind drives which menuType API result we render. If the user is
    // viewing a charity portal but isAdvisor is true (multi-portal user), we still want
    // the charity menu - menuItems already reflect that because getMenuType used activePortal.
    if (activePortal?.kind === 'advisor' && !isAdvisorImpersonating && menuItems.length > 0) {
      return menuItems.map((item) => ({
        to: item.route || '/funds',
        label: item.label,
        'data-testid': item.key?.includes('dashboard') ? 'navBarMyGivingFundLink' : undefined,
      }))
    }

    // For advisors (not being impersonated as a donor client), use dynamic menu items (no fundId needed for advisor routes)
    if (isAdvisor && !isAdvisorImpersonating && menuItems.length > 0 && !activePortal) {
      return menuItems.map((item) => ({
        to: item.route || '/funds',
        label: item.label,
        'data-testid': item.key?.includes('dashboard') ? 'navBarMyGivingFundLink' : undefined,
      }))
    }

    // Use dynamic menu items if available and we have a fundId
    if (menuItems.length > 0 && currentFundId) {
      return menuItems.map((item) => {
        // Replace route placeholders with actual values
        const route = (item.route || '').replace(':fundId', currentFundId || '').replace(':charityId', charityId || '')

        return {
          to: route,
          label: item.label,
          'data-testid': item.key?.includes('dashboard') ? 'navBarMyGivingFundLink' : undefined,
        }
      })
    }

    // Fallback to hardcoded menu if no items from API.
    // FLIGHT-570: prefer activePortal kind over userType so multi-portal users
    // see the right fallback when API menu hasn't loaded yet.
    if (effectivePortal?.kind === 'charity') {
      return [
        { to: `/charities/${charityId}`, label: 'Dashboard', 'data-testid': 'navBarMyGivingFundLink' },
        { to: `/charities/${charityId}/activity`, label: 'Activity' },
        { to: `/charities/${charityId}/designations`, label: 'Designations ' },
        { to: `/charities/${charityId}/direct-deposit`, label: 'Direct Deposit' },
        { to: `/charities/${charityId}/confirm`, label: 'Charity Profile' },
        { to: `/charities/${charityId}/invite-users`, label: 'Invite Users' },
        { to: `/charities/${charityId}/givegenius`, label: 'GiveGenius' },
      ]
    }

    return [
      { to: `/funds/${currentFundId}`, label: 'Dashboard', 'data-testid': 'navBarMyGivingFundLink' },
      { to: `/funds/${currentFundId}/activity`, label: 'Activity' },
      { to: `/funds/${currentFundId}/contribute`, label: 'Add Funds' },
      { to: `/funds/${currentFundId}/grant`, label: 'Give' },
      { to: `/funds/${currentFundId}/share-funds-with-others`, label: 'Share Funds' },
      { to: `/funds/${currentFundId}/documents`, label: 'Tax Receipts' },
      // Show Administration tab if user has legacy admin flag OR RBAC manage:access/system:admin permission
      ...(isAdmin || hasManageAccess ? [{ to: '/management', label: 'Administration' }] : []),
    ]
  }, [
    menuItems,
    currentFundId,
    charityId,
    isAdmin,
    hasManageAccess,
    isAdvisor,
    isAdvisorImpersonating,
    advisorLoading,
    menuLoading,
    effectivePortal,
  ])

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleSignOut = async (_e) => {
    await logout()
  }

  const isAdvisorImpersonation = impersonationType === 'ADVISOR'

  return (
    <AppBar position="sticky" elevation={0}>
      <Container
        maxWidth={isCharityUser ? '' : 'xl'}
        sx={{ mb: 0, paddingLeft: { xs: 0, md: 4 }, maxWidth: isCharityUser ? '80%' : 'auto' }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'flex-end' }}>
          {showNavbarLogo && (
            <Box sx={{ mr: 3, display: { xs: 'none', md: 'flex' } }}>
              <img
                src={logoSrc}
                alt={branding?.platformName || 'Logo'}
                style={{
                  maxHeight: '40px',
                  maxWidth: '180px',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            <NavTabs
              tabs={fundLinks}
              mobileMenuButton
              breakpoint="lg"
              TabIndicatorProps={{
                style: {
                  backgroundColor: theme.palette.primary.contrastText,
                },
              }}
              tabProps={{
                sx: (t) => ({
                  color: t.palette.primary.contrastText,
                  '&:hover': {
                    color: t.palette.primary.contrastText,
                    opacity: 0.8,
                  },
                  '&.Mui-selected': {
                    color: t.palette.primary.contrastText,
                  },
                }),
              }}
            />
          </Box>
          {!isAdvisorImpersonation && <ContextSwitcher />}
          {!isAdvisorImpersonation && (
            <Box justifySelf="flex-end">
              <Button
                data-testid="avatarIcon"
                variant="contained"
                sx={{
                  minWidth: 0,
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  width: '40px!important',
                  height: '40px',
                  borderRadius: '20px',
                  padding: 0,
                }}
                onClick={handleOpenUserMenu}
              >
                <Icons.Person />
                <Text.SR>user menu</Text.SR>
              </Button>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {userLinks.map((link) => {
                  let { to } = link

                  if (link.to === '/profile') {
                    if (effectivePortal?.kind === 'charity' && charityId) {
                      to = `/charities/${charityId}/my-profile`
                    } else if (effectivePortal?.kind === 'advisor') {
                      to = `/advisor/my-profile`
                    } else if (effectivePortal?.kind === 'fund' && effectivePortal.id) {
                      to = `/profile/${effectivePortal.id}`
                    }
                  }

                  return (
                    <MenuItem key={link.to} onClick={handleCloseUserMenu}>
                      <Link to={to} lable={link.label} data-testid={`user${to}`}>
                        {link.label}
                      </Link>
                    </MenuItem>
                  )
                })}
                <MenuItem key="signout" onClick={handleCloseUserMenu}>
                  <Typography onClick={handleSignOut} data-testid="signOutLink">
                    Sign Out
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  )
}

// FLIGHT-587: FundSelectMenu removed. Replaced by ContextSwitcher
// (FLIGHT-369) which handles portal switching across donor / charity /
// advisor / admin in a single dropdown.
